import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Notification, NotificationClient, NotificationType } from 'src/api-service';
import { AppCacheService } from 'src/app/shared/util/app-cache.service';
import { APP_VERSION } from 'src/environments/version';
import { normalizeError } from '../../app-store-error.types';
import { UserActions } from '../../user/user.actions';
import { selectUserContext } from '../../user/user.selectors';
import { NotificationActions } from './notification.actions';
import {
    selectPendingAppReload,
    selectSelectedNotification,
    selectUnreadNotifications,
} from './notification.selectors';

// **************************************************************************
// NOTIFICATION EFFECTS
// Handles all notification API operations
// **************************************************************************

@Injectable()
export class NotificationEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly notificationClient = inject(NotificationClient);
    private readonly appCache = inject(AppCacheService);

    // =========================================================================
    // INITIALIZATION (triggered after user login)
    // =========================================================================
    initializeOnUserLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.initializeUserSuccess),
            map(({ selectedCompany }) =>
                NotificationActions.initialize({
                    countryCode: selectedCompany?.countryCode,
                    companyId: selectedCompany?.id,
                }),
            ),
        ),
    );

    /**
     * Login only fetches the bell payload (5 unread + 3 history).
     * Release notes are up to 50 rows of full HTML and are only needed on the
     * release notes page, which loads them itself.
     */
    initialize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.initialize),
            map(({ countryCode, companyId }) =>
                NotificationActions.loadUserNotifications({ countryCode, companyId }),
            ),
        ),
    );

    // =========================================================================
    // USER NOTIFICATIONS (Bell icon)
    // =========================================================================
    loadUserNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadUserNotifications),
            switchMap(({ countryCode, companyId }) =>
                this.notificationClient.getUserNotifications(5, 3, countryCode, companyId).pipe(
                    map(result =>
                        NotificationActions.loadUserNotificationsSuccess({
                            unread: result.unread,
                            history: result.history,
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.loadUserNotificationsFailure({ error: normalizeError(error), toastKey: "NOTIFICATION.TOAST.LOAD_USER_NOTIFICATIONS_ERROR" })),
                    ),
                ),
            ),
        ),
    );

    /** Auto-show first unread notification after login (if dialogPopupAtStart) */
    autoShowUnreadNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadUserNotificationsSuccess),
            delay(1000), // Small delay to let UI settle
            withLatestFrom(this.store.select(selectUnreadNotifications)),
            filter(([_, unread]) => {
                const now = new Date();
                return unread.some(
                    n => n.dialogPopupAtStart && (!n.expiresAt || new Date(n.expiresAt) > now),
                );
            }),
            map(([_, unread]) => {
                const now = new Date();
                const popupNotification = unread.find(
                    n => n.dialogPopupAtStart && (!n.expiresAt || new Date(n.expiresAt) > now),
                );
                return NotificationActions.selectNotification({
                    id: popupNotification!.id,
                    isUnread: true,
                });
            }),
        ),
    );

    // =========================================================================
    // RELEASE NOTES
    // =========================================================================
    loadReleaseNotes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadReleaseNotes),
            switchMap(({ isDeveloper }) =>
                this.notificationClient.getReleases(50, isDeveloper).pipe(
                    map(notifications =>
                        NotificationActions.loadReleaseNotesSuccess({ notifications }),
                    ),
                    catchError(error =>
                        of(NotificationActions.loadReleaseNotesFailure({ error: normalizeError(error), toastKey: "NOTIFICATION.TOAST.LOAD_RELEASE_NOTES_ERROR" })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // ADMIN LIST (Developer section)
    // =========================================================================
    loadAdminList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadAdminList),
            switchMap(() =>
                this.notificationClient.getAllList(true).pipe(
                    map(notifications =>
                        NotificationActions.loadAdminListSuccess({ notifications }),
                    ),
                    catchError(error =>
                        of(NotificationActions.loadAdminListFailure({ error: normalizeError(error), toastKey: "NOTIFICATION.TOAST.LOAD_ADMIN_LIST_ERROR" })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // LOAD BY ID
    // =========================================================================
    loadById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadById),
            switchMap(({ id }) =>
                this.notificationClient.getById(id).pipe(
                    map(notification =>
                        NotificationActions.loadByIdSuccess({ notification }),
                    ),
                    catchError(error =>
                        of(NotificationActions.loadByIdFailure({ error: normalizeError(error), toastKey: "NOTIFICATION.TOAST.LOAD_BY_ID_ERROR" })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // MARK AS READ
    // =========================================================================
    markAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.markAsRead),
            mergeMap(({ id }) =>
                this.notificationClient.markAsRead(id).pipe(
                    map(readStatus =>
                        NotificationActions.markAsReadSuccess({ id, readStatus }),
                    ),
                    catchError(error =>
                        of(NotificationActions.markAsReadFailure({ id, error: normalizeError(error) })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // CREATE
    // =========================================================================
    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.create),
            mergeMap(({ notification }) =>
                this.notificationClient.create(notification).pipe(
                    map(created =>
                        NotificationActions.createSuccess({
                            notification: created,
                            toastKey: 'NOTIFICATION.TOAST.CREATE_SUCCESS',
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.createFailure({
                            error: normalizeError(error),
                            toastKey: 'NOTIFICATION.TOAST.CREATE_FAILURE',
                        })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // UPDATE
    // =========================================================================
    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.update),
            mergeMap(({ id, notification }) =>
                this.notificationClient.update(id, notification).pipe(
                    map(updated =>
                        NotificationActions.updateSuccess({
                            notification: updated,
                            toastKey: 'NOTIFICATION.TOAST.UPDATE_SUCCESS',
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.updateFailure({
                            id,
                            error: normalizeError(error),
                            toastKey: 'NOTIFICATION.TOAST.UPDATE_FAILURE',
                        })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // PUBLISH
    // =========================================================================
    publish$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.publish),
            mergeMap(({ id }) =>
                this.notificationClient.publish(id).pipe(
                    map(notification =>
                        NotificationActions.publishSuccess({
                            notification,
                            toastKey: 'NOTIFICATION.TOAST.PUBLISH_SUCCESS',
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.publishFailure({
                            id,
                            error: normalizeError(error),
                            toastKey: 'NOTIFICATION.TOAST.PUBLISH_FAILURE',
                        })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // UNPUBLISH
    // =========================================================================
    unpublish$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.unpublish),
            mergeMap(({ id }) =>
                this.notificationClient.unpublish(id).pipe(
                    map(notification =>
                        NotificationActions.unpublishSuccess({
                            notification,
                            toastKey: 'NOTIFICATION.TOAST.UNPUBLISH_SUCCESS',
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.unpublishFailure({
                            id,
                            error: normalizeError(error),
                            toastKey: 'NOTIFICATION.TOAST.UNPUBLISH_FAILURE',
                        })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // DELETE
    // =========================================================================
    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.delete),
            mergeMap(({ id }) =>
                this.notificationClient.delete(id).pipe(
                    map(() =>
                        NotificationActions.deleteSuccess({
                            id,
                            toastKey: 'NOTIFICATION.TOAST.DELETE_SUCCESS',
                        }),
                    ),
                    catchError(error =>
                        of(NotificationActions.deleteFailure({
                            id,
                            error: normalizeError(error),
                            toastKey: 'NOTIFICATION.TOAST.DELETE_FAILURE',
                        })),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // REFRESH AFTER MUTATIONS
    // =========================================================================
    /**
     * Refresh admin list, user notifications and release notes after every mutation.
     * Create/update are included so the list and the preview show the saved version
     * without the user having to reload the page.
     */
    refreshAfterMutation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                NotificationActions.createSuccess,
                NotificationActions.updateSuccess,
                NotificationActions.publishSuccess,
                NotificationActions.unpublishSuccess,
                NotificationActions.deleteSuccess,
            ),
            withLatestFrom(this.store.select(selectUserContext)),
            mergeMap(([_, userContext]) => [
                NotificationActions.loadAdminList(),
                NotificationActions.loadUserNotifications({
                    countryCode: userContext?.selectedCompany?.countryCode,
                    companyId: userContext?.selectedCompany?.id,
                }),
                NotificationActions.loadReleaseNotes({
                    isDeveloper: userContext?.userRoles?.developer ?? false,
                }),
            ]),
        ),
    );

    // =========================================================================
    // APP CACHE (notification announcing a newer frontend build)
    // =========================================================================
    /** Clicking a release/hotfix notice for another version drops the cached build */
    clearAppCacheOnOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.selectNotification),
            withLatestFrom(this.store.select(selectSelectedNotification)),
            filter(([_, notification]) => this.announcesNewBuild(notification)),
            switchMap(() =>
                from(this.appCache.clear()).pipe(
                    map(() => NotificationActions.appCacheCleared()),
                ),
            ),
        ),
    );

    /** Reload once the user is done reading, so the cleared cache is refilled */
    reloadAfterDismiss$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                NotificationActions.markAsReadSuccess,
                NotificationActions.clearSelection,
            ),
            withLatestFrom(this.store.select(selectPendingAppReload)),
            filter(([_, pendingAppReload]) => pendingAppReload),
            map(() => NotificationActions.reloadApp()),
        ),
    );

    reloadApp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.reloadApp),
            tap(() => this.appCache.reload()),
        ),
        { dispatch: false },
    );

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    /** A release/hotfix notice naming a version other than the one running */
    private announcesNewBuild(notification: Notification | null | undefined): boolean {
        if (!notification?.version) return false;

        const isBuildNotice = notification.type === NotificationType.Release
            || notification.type === NotificationType.Hotfix;

        return isBuildNotice && notification.version !== APP_VERSION;
    }
}
